<template>
  <mds-section border="tertiary" bold :size="8" :title="translate('transactionFee')">
    <template v-slot:mds-section-actions>
      <mds-switch
        v-model="includeTransactionFee"
        hidden-label
        e2e="include-transaction-fee-switch"
        value=""
        @click.stop
        @change="toggleTransactionFee"
      >
        {{ translate('include') }}
      </mds-switch>
    </template>
    <template v-slot:default>
      <mds-form v-if="includeTransactionFee" size="small">
        <InfoIconField
          :show-info-icons="settings.showInfoIcons"
          show-learn-more
          :abbr-content="translate('frontLoadAbbrMsg')"
          :detailed-content="tryTranslate('frontLoadDetailMsg')"
          :detailed-sections="frontLoadMoreInfo"
          :labels="translatedLabels()"
        >
          <mds-fieldset size="small" variation="radio-group" :legend="translate('frontLoad')">
            <mds-radio-button
              v-for="(item, index) in frontLoadTypeOptions"
              :key="index"
              v-model="frontLoadType"
              name="frontLoad"
              size="small"
              :value="item.value"
              :class="$style['front-load-radio']"
              @input="handleFrontLoadFeeChanged"
            >
              <template v-slot:mds-microcopy>
                <template v-if="item.hasMicrocopy">
                  <mds-fieldset horizontal :class="$style['front-load-fieldset']">
                    <mds-input
                      v-if="item.frontLoadAmountType === 0"
                      size="small"
                      hidden-label
                      :label="item.text"
                      :disabled="item.value !== frontLoadType"
                      :value="formatter.number(customAmount)"
                      @change="handleFrontLoadAmountChanged"
                    >
                    </mds-input>
                    <mds-input
                      v-else
                      size="small"
                      hidden-label
                      :label="item.text"
                      :disabled="item.value !== frontLoadType"
                      :value="formatter.number(customPercent, 'longDecimal')"
                      @change="handleFrontLoadPercentChanged"
                    >
                    </mds-input>
                    <mds-button-group
                      size="small"
                      :content="frontLoadAmountTypeOptions"
                      @mds-button-group-item-active="handleFrontLoadAmountTypeChanged"
                    ></mds-button-group>
                  </mds-fieldset>
                </template>
              </template>
              {{ item.text }}
            </mds-radio-button>
          </mds-fieldset>
        </InfoIconField>
        <roa-loi
          :mwc-id="getMwcIdForChild('roaloi')"
          :roaloi-data="roaloiData"
          :currency-symbol="currencySymbol"
          :show-roa-loi="showRoaLoi"
          @amount-changed="$emit('roa-loi-changed', $event)"
          @roa-loi-removed="$emit('roa-loi-removed')"
        ></roa-loi>
        <InfoIconField
          :show-info-icons="settings.showInfoIcons"
          :show-learn-more="false"
          :abbr-content="tryTranslate('redempAndDerferredAbbrMsg')"
          :abbr-sections="redempAndDeferredAbbrInfo"
        >
          <mds-fieldset
            size="small"
            variation="radio-group"
            :legend="translate('applyRedemptionOrDeferredLoad')"
          >
            <mds-radio-button
              v-for="(item, index) in redemptionChargeOptions"
              :key="index"
              v-model="redemptionCharge"
              size="small"
              name="redemptionCharge"
              :e2e="`redemption-charge-${item.text}`"
              :value="item.value"
              @input="handleRedemptionChargeChanged"
            >
              {{ item.text }}
            </mds-radio-button>
          </mds-fieldset>
        </InfoIconField>
        <mds-fieldset
          v-if="showChargeForRebalancing"
          size="small"
          variation="radio-group"
          :legend="translate('applyForRebalancing')"
        >
          <mds-radio-button
            v-for="(item, index) in applyTransactionFeesOptions"
            :key="index"
            v-model="chargeOnRebalancing"
            name="chargeOnRebalancing"
            size="small"
            :e2e="`charge-on-rebalancing-${item.text}`"
            :value="item.value"
            @input="handleChargeOnRebalancingChanged"
          >
            {{ item.text }}
          </mds-radio-button>
        </mds-fieldset>
      </mds-form>
    </template>
  </mds-section>
</template>

<script>
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import MdsForm from '@mds/form';
import MdsFieldset from '@mds/fieldset';
import MdsSection from '@mds/section';
import MdsSwitch from '@mds/switch';
import MdsInput from '@mds/input';
import MdsRadioButton from '@mds/radio-button';
import MdsButtonGroup from '@mds/button-group';
import InfoIconField from 'hypo-shared-utils/src/components/InfoIconField';
import { parseNumber, isInvalidAmount, isInvalidPercentage } from '../../utils/utils';
import RoaLoi from './ROALOI';
import { FRONT_LOAD_TYPES, FRONT_LOAD_AMOUNT_TYPES } from '../../utils/constants';
import {
  booleanOptions,
  redemptionChargeOptions,
  vahRedemptionChargeOptions,
  frontLoadTypeOptions,
} from '../../utils/input-options';

const { CUSTOM, LOI, VAH } = FRONT_LOAD_TYPES;
const { AMOUNT, PERCENT } = FRONT_LOAD_AMOUNT_TYPES;

export default {
  name: 'transaction-fee',
  components: {
    MdsForm,
    MdsFieldset,
    MdsSection,
    MdsSwitch,
    MdsInput,
    MdsRadioButton,
    MdsButtonGroup,
    RoaLoi,
    InfoIconField,
  },
  mixins: [MwcVueHelperMixin, MwcConfigMixin],
  props: {
    transactionFeeSetting: Object,
    showLoiAsFrontLoad: {
      type: Boolean,
      default: false,
    },
    showChargeForRebalancing: {
      type: Boolean,
      default: true,
    },
    currencySymbol: {
      type: String,
      default: '$',
    },
    fundFamilies: {
      type: Array,
      default() {
        return [];
      },
    },
    showRoaLoi: {
      type: Boolean,
      default: true,
    },
    roaloiData: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    const {
      includeTransactionFee = false,
      frontLoadType = 0,
      frontLoadAmountType = 1,
      redemptionCharge = 1,
      chargeOnRebalancing,
    } = this.transactionFeeSetting || {};
    return {
      defaultConfig: {
        format: {
          longDecimal: {
            dataType: 'number',
            minimumFractionDigits: 0,
            maximumFractionDigits: 4,
          },
        },
      },
      includeTransactionFee,
      redemptionCharge,
      chargeOnRebalancing,
      frontLoadType,
      frontLoadAmountType,
      customPercent: 0,
      customAmount: 0,
    };
  },
  computed: {
    frontLoadFeeType() {
      if (this.frontLoadType === CUSTOM) {
        return `${this.frontLoadType}-${this.frontLoadAmountType}`;
      }
      return `${this.frontLoadType}`;
    },
    frontLoadTypeOptions() {
      return frontLoadTypeOptions
        .filter(i => {
          return this.frontLoadType === VAH || i.value !== VAH;
        })
        .filter(i => {
          return this.showLoiAsFrontLoad || i.value !== LOI;
        })
        .map(({ labelKey, value }) => {
          const hasMicrocopy = value === CUSTOM;
          const text = this.translate(labelKey);
          if (hasMicrocopy) {
            const frontLoadAmountType = this.frontLoadAmountType;
            return {
              text,
              value,
              frontLoadAmountType,
              hasMicrocopy,
              microcopyInputValue: this.isCustomAmount ? this.customAmount : this.customPercent,
            };
          }
          return {
            text,
            value,
          };
        });
    },
    frontLoadAmountTypeOptions() {
      return [
        {
          text: '%',
          value: PERCENT,
          active: this.frontLoadAmountType === PERCENT,
          disabled: this.frontLoadType !== CUSTOM,
        },
        {
          text: this.currencySymbol,
          value: AMOUNT,
          active: this.frontLoadAmountType === AMOUNT,
          disabled: this.frontLoadType !== CUSTOM,
        },
      ];
    },
    redemptionChargeOptions() {
      const radioOptions =
        this.redemptionCharge === 2 ? vahRedemptionChargeOptions : redemptionChargeOptions;
      return this.translateComponentOptions(radioOptions);
    },
    applyTransactionFeesOptions() {
      return this.translateComponentOptions(booleanOptions);
    },
    isCustomAmount() {
      return this.frontLoadAmountType === AMOUNT;
    },
    frontLoadMoreInfo() {
      return [
        {
          title: this.translate('letterOfIntent'),
          content: this.translate('loiMsg'),
        },
        {
          title: this.translate('rightOfAcc'),
          content: this.translate('roaMsg'),
        },
      ];
    },
    redempAndDeferredAbbrInfo() {
      return [
        {
          title: this.translate('redemption'),
          content: this.translate('redemptionMsg'),
        },
        {
          title: this.translate('deferredLoad'),
          content: this.translate('deferredLoadMsg'),
        },
      ];
    },
  },
  watch: {
    transactionFeeSetting() {
      const {
        includeTransactionFee,
        frontLoadType,
        frontLoadAmountType,
        frontLoadAmount,
        redemptionCharge,
        chargeOnRebalancing,
      } = this.transactionFeeSetting;
      this.includeTransactionFee = includeTransactionFee;
      this.frontLoadType = frontLoadType;
      this.redemptionCharge = redemptionCharge;
      this.chargeOnRebalancing = chargeOnRebalancing;
      if (typeof frontLoadAmountType === 'undefined' || typeof frontLoadAmount === 'undefined') {
        return;
      }
      this.frontLoadAmountType = frontLoadAmountType;
      if (this.isCustomAmount) {
        this.customAmount = frontLoadAmount;
      } else {
        this.customPercent = frontLoadAmount;
      }
    },
  },
  methods: {
    tryTranslate(key) {
      if (this.labeller.exists(key)) {
        return this.translate(key);
      }
      return '';
    },
    translateComponentOptions(options) {
      return options.map(({ labelKey, value }) => {
        return {
          text: this.translate(labelKey),
          value,
        };
      });
    },
    toggleTransactionFee() {
      this.$emit('include-transaction-fee-changed', this.includeTransactionFee);
    },
    handleFrontLoadAmountTypeChanged(e) {
      this.frontLoadAmountType = Number(e.target.value);
      // reset
      if (this.isCustomAmount) {
        this.customPercent = 0;
      } else {
        this.customAmount = 0;
      }
      this.handleFrontLoadFeeChanged();
    },
    handleFrontLoadFeeChanged() {
      let frontLoadFeeData = {
        frontLoadFeeType: this.frontLoadFeeType,
      };
      const isCustomFrontLoad = this.frontLoadType === CUSTOM;
      if (isCustomFrontLoad) {
        frontLoadFeeData = {
          ...frontLoadFeeData,
          frontLoadAmount: this.isCustomAmount ? this.customAmount : this.customPercent,
        };
      }
      this.$emit('front-load-changed', frontLoadFeeData);
    },
    handleFrontLoadAmountChanged(e) {
      const amount = parseNumber(e.target.value, 2);
      if (isInvalidAmount(amount) || amount === this.customAmount) {
        e.target.value = this.formatter.number(this.customAmount);
        return;
      }
      this.customAmount = amount;
      this.handleFrontLoadFeeChanged();
    },
    handleFrontLoadPercentChanged(e) {
      const percentage = parseNumber(e.target.value, 4);
      if (isInvalidPercentage(percentage) || percentage === this.customPercent) {
        e.target.value = this.formatter.number(this.customPercent, 'longDecimal');
        return;
      }
      this.customPercent = percentage;
      this.handleFrontLoadFeeChanged();
    },
    handleRedemptionChargeChanged() {
      this.$emit('redemption-charge-changed', this.redemptionCharge);
    },
    handleChargeOnRebalancingChanged() {
      this.$emit('charge-on-rebalancing-changed', this.chargeOnRebalancing);
    },
    translatedLabels() {
      return {
        learnMore: this.translate('learnMore'),
        close: this.translate('close'),
        modalTitle: this.translate('moreInformation'),
      };
    },
  },
};
</script>

<style lang="scss" module>
.front-load-radio {
  .mds-radio-button__visible-wrap {
    flex: 1 1 100%;
    .mds-radio-button__text {
      flex: 1 1 100%;
    }
    .front-load-fieldset .mds-label {
      flex: 1 1 80%;
    }
  }
}
</style>
